'use client'

import { useEffect } from 'react'
import { atom, useAtom, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import { atomFamily } from 'jotai-family'
import { AddressAutoCompleteField } from '@/features/addressAutocomplete/AddressAutoCompleteField'
import { useRegistrationAddressState } from '@/features/priceCalculator/usePriceCalculatorForm'
import { usePriceIntentValueOrThrow } from '@/features/priceCalculator/usePriceIntent'
import type { MaskedAddressField as MaskedAddressFieldType } from '@/services/PriceCalculator/Field.types'
import { Text } from 'ui/src/components/Text/Text'
import { TextField } from 'ui/src/components/TextField/TextField'
import { CrossIcon } from 'ui/src/icons/CrossIcon'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { fakeInput } from '../ProductPurchaseForm/SsnField/SsnField.css'

const zipCodeAtomFamily = atomFamily((_priceIntentId: string) => atom<string>(''))

type MaskedTextFieldProps = {
  field: Omit<MaskedAddressFieldType, 'label'>
  label: string
  autoFocus?: boolean
}

export function MaskedAddressAutoCompleteField({ field, label, autoFocus }: MaskedTextFieldProps) {
  const priceIntent = usePriceIntentValueOrThrow()

  const [useRegistrationAddress, setUseRegistrationAddress] = useRegistrationAddressState(priceIntent.id)

  if (useRegistrationAddress) {
    return (
      <MaskedFakeInput
        onClear={() => setUseRegistrationAddress(false)}
        label={label}
        value={field.name === 'street' ? priceIntent.suggestedData?.street : priceIntent.suggestedData?.zipCode}
      />
    )
  }

  // Only restore stored value when the user has their own data (containsSuggestedData=false).
  // When containsSuggestedData=true the stored value is masked PII — keep the field empty
  // so the user types a fresh address after opting out of the registration address.
  const defaultValue = priceIntent.data.containsSuggestedData ? undefined : field.defaultValue
  const Field = field.name === 'zipCode' ? ZipCodeField : AddressField
  return <Field priceIntentId={priceIntent.id} label={label} field={{ ...field, defaultValue }} autoFocus={autoFocus} />
}

function MaskedFakeInput({ label, value, onClear }: { label: string; value: string; onClear: () => void }) {
  return (
    <div className={fakeInput}>
      <div className={sprinkles({ flexGrow: 1, overflow: 'hidden' })}>
        <Text size="xs" color="textSecondary">
          {label}
        </Text>
        <Text color="textSecondary" size="md">
          {value}
        </Text>
      </div>
      <button
        className={sprinkles({ cursor: 'pointer' })}
        type="button"
        onClick={onClear}
        aria-hidden={true}
        tabIndex={-1}
      >
        <CrossIcon size="1.5rem" />
      </button>
    </div>
  )
}

function ZipCodeField({ priceIntentId, field, label, autoFocus }: MaskedTextFieldProps & { priceIntentId: string }) {
  useHydrateAtoms([[zipCodeAtomFamily(priceIntentId), field.defaultValue ?? '']])
  const [zipCode, setZipCode] = useAtom(zipCodeAtomFamily(priceIntentId))

  useEffect(() => {
    // Syncs the default value with the atom as field.defaultValue should be the
    // source of truth for price calculator fields.
    return () => setZipCode(field.defaultValue ?? '')
  }, [field.defaultValue, setZipCode])

  return (
    <TextField
      type="text"
      name={field.name}
      value={zipCode}
      onValueChange={setZipCode}
      label={label}
      pattern={field.pattern}
      minLength={field.minLength}
      maxLength={field.maxLength}
      inputMode={field.inputMode ?? 'text'}
      required={field.required}
      autoFocus={autoFocus}
    />
  )
}

function AddressField({ priceIntentId, field, label, autoFocus }: MaskedTextFieldProps & { priceIntentId: string }) {
  const setZipCode = useSetAtom(zipCodeAtomFamily(priceIntentId))

  return (
    <AddressAutoCompleteField
      label={label}
      name={field.name}
      defaultValue={field.defaultValue}
      autoFocus={autoFocus}
      onAddressSelect={(address) => {
        if (address.zipCode) {
          setZipCode(address.zipCode)
        }
      }}
    />
  )
}
